/**
 * Maps a checkpoint row (`YourResponsibilityChecklistTable` `data` item) into the
 * `sections` shape consumed by `AsEmployeeCheckpointDrawer`.
 *
 * Kept isolated so the checkpoint payload → drawer mapping is the only thing to
 * update when the payload evolves.
 */

import {
  formatArticleHtml,
  parseArticleBodyPayload,
  paragraphsToArticleHtml,
  shouldPreferArticleHtml,
} from "@components/utils/formatArticleHtml";

const norm = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

/** Checkpoint instructions are stored as HTML; the drawer renders plain paragraphs. */
export function htmlToParagraphs(value) {
  return String(value ?? "")
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\/\s*(p|div|li|h[1-6])\s*>/gi, "\n\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

/** Inspection protocols arrive either parsed or as a JSON string. */
function parseMaybeJson(value) {
  if (value == null || value === "") return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function extractTipTapParagraphs(doc) {
  const paragraphs = [];
  function walk(node) {
    if (!node || typeof node !== "object") return;
    if (node.type === "paragraph" && Array.isArray(node.content)) {
      const text = node.content
        .map((part) => part?.text || "")
        .join("")
        .trim();
      if (text) paragraphs.push(text);
    }
    if (Array.isArray(node.content)) node.content.forEach(walk);
  }
  walk(doc);
  return paragraphs;
}

function extractArticleContent(activeArticle) {
  const parsed = parseMaybeJson(activeArticle) ?? activeArticle;
  if (!parsed) return [];
  if (typeof parsed === "string") return htmlToParagraphs(parsed);
  if (parsed.body) {
    const body = parseMaybeJson(parsed.body) ?? parsed.body;
    if (typeof body === "string") return htmlToParagraphs(body);
    if (body?.content) return extractTipTapParagraphs(body);
  }
  if (parsed.content) {
    if (typeof parsed.content === "string") return htmlToParagraphs(parsed.content);
    if (parsed.content?.content) return extractTipTapParagraphs(parsed.content);
  }
  if (parsed.text) return [String(parsed.text).trim()].filter(Boolean);
  return [];
}

function resolveArticleBody(activeArticle) {
  const parsed = parseMaybeJson(activeArticle) ?? activeArticle;
  if (!isPlainObject(parsed)) return null;
  const body = parseArticleBodyPayload(parsed.body);
  if (!body || shouldPreferArticleHtml(body)) return null;
  return body;
}

function resolveArticleHtml(item, articleSource, paragraphFallback = []) {
  const sources = [
    item?.active_knowledge_article_html,
    item?.knowledge_article_html,
    articleSource,
  ];
  for (const source of sources) {
    if (source == null || source === "") continue;
    const html = formatArticleHtml(source);
    if (html && html !== "<p></p>") return html;
  }
  if (Array.isArray(paragraphFallback) && paragraphFallback.length) {
    return paragraphsToArticleHtml(paragraphFallback);
  }
  return "<p></p>";
}

function buildArticleSection({
  heading,
  content,
  articleHtml,
  articleBody,
  languages,
  selectedLanguage,
}) {
  return {
    id: "article",
    type: "article",
    title: "Read this article",
    heading: heading || undefined,
    content,
    articleHtml,
    articleBody,
    languages: languages || [],
    selectedLanguage: selectedLanguage || null,
  };
}

/** IQ payloads store overview as a string, `{ overview }`, or nested `{ overview: { overview } }`. */
function overviewTextFromSummaryField(summary) {
  if (summary == null) return "";
  if (typeof summary === "string") return summary.trim();
  if (typeof summary !== "object" || Array.isArray(summary)) return "";

  const nested = summary.overview ?? summary.videoSummary;
  if (typeof nested === "string") return nested.trim();
  if (nested && typeof nested === "object") {
    const inner = nested.overview ?? nested.videoSummary;
    if (typeof inner === "string") return inner.trim();
  }
  return "";
}

function resolveVideoSummary(microlearning, item) {
  const inspection =
    parseMaybeJson(item?.active_inspection_instructions) ?? item?.active_inspection_instructions;
  const inspectionSummary =
    inspection && typeof inspection === "object" && !Array.isArray(inspection)
      ? String(inspection.videoSummary ?? "").trim()
      : "";

  const text =
    overviewTextFromSummaryField(microlearning?.microlearning_summary) ||
    String(microlearning?.table_of_contents?.videoSummary ?? "").trim() ||
    String(microlearning?.videoSummary ?? "").trim() ||
    overviewTextFromSummaryField(microlearning?.summary) ||
    inspectionSummary;

  if (text) return { overview: text };

  const summary = microlearning?.summary;
  if (
    summary &&
    typeof summary === "object" &&
    !Array.isArray(summary) &&
    Object.keys(summary).length > 0
  ) {
    return summary;
  }

  return null;
}

function extractVideoUrlFromIframeHtml(value) {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed.toLowerCase().includes("<iframe")) return "";
  const match = trimmed.match(/\ssrc=["']([^"']+)["']/i);
  return match?.[1]?.trim() || "";
}

const BARE_CLOUDFLARE_STREAM_UID_RE = /^[a-f0-9]{32}$/i;

function isBareCloudflareStreamUid(value) {
  if (value == null || value === "") return false;
  return BARE_CLOUDFLARE_STREAM_UID_RE.test(String(value).trim());
}

/** Strip timestamp query/hash from a chapter jump URL to get the base iframe playback URL. */
function extractBaseVideoUrlFromJumpUrl(jumpUrl) {
  if (!jumpUrl || typeof jumpUrl !== "string") return "";
  const trimmed = jumpUrl.trim();
  if (!trimmed) return "";
  return trimmed.split("#")[0].split("?")[0].replace(/\/+$/, "");
}

function collectMicrolearningJumpUrlSources(microlearning) {
  if (!microlearning || typeof microlearning !== "object") return [];
  return [
    ...(microlearning?.table_of_contents?.chapters ?? []),
    ...(microlearning?.chapters ?? []),
    ...(microlearning?.steps ?? []),
    ...(microlearning?.inspection_instructions?.steps ?? []),
  ];
}

/** Prefer full iframe URLs stored on TOC chapters / steps when top-level video fields are empty. */
export function extractPlaybackUrlFromMicrolearningInstructions(microlearning) {
  for (const entry of collectMicrolearningJumpUrlSources(microlearning)) {
    const base = extractBaseVideoUrlFromJumpUrl(entry?.video_jump_url);
    if (base) return base;
  }
  return "";
}

function resolveCloudflareIframeFromUid(uid, referenceUrl = "") {
  if (!isBareCloudflareStreamUid(uid)) return "";
  const normalizedUid = String(uid).trim();
  const hostMatch = String(referenceUrl).match(/\b(customer-[a-z0-9]+\.cloudflarestream\.com)\b/i);
  if (hostMatch) {
    return `https://${hostMatch[1]}/${normalizedUid}/iframe`;
  }
  return `https://iframe.videodelivery.net/${normalizedUid}`;
}

/** True when a value is the video-protocol JSON (new TOC or migrated steps/summary). */
function isMicrolearningInstructionsObject(value) {
  if (!isPlainObject(value)) return false;
  if (Array.isArray(value.steps) && value.steps.length > 0) return true;
  if (Array.isArray(value.chapters) && value.chapters.length > 0) return true;
  if (
    Array.isArray(value.table_of_contents?.chapters) &&
    value.table_of_contents.chapters.length > 0
  ) {
    return true;
  }
  if (String(value.video_url ?? value.video ?? "").trim()) return true;
  if (isPlainObject(value.summary) && Object.keys(value.summary).length > 0) return true;
  if (
    isPlainObject(value.microlearning_summary) &&
    Object.keys(value.microlearning_summary).length > 0
  ) {
    return true;
  }
  return false;
}

function isMicrolearningInstructionsPayload(value) {
  if (value == null || value === "") return false;
  const parsed = typeof value === "object" ? value : parseMaybeJson(value);
  return isMicrolearningInstructionsObject(parsed);
}

function resolveVideoMicrolearningPayload(item) {
  const candidates = [
    parseMaybeJson(item?.microlearning_instructions) ?? item?.microlearning_instructions,
    parseMaybeJson(item?.active_video_microlearning) ?? item?.active_video_microlearning,
  ].filter((payload) => payload && typeof payload === "object" && !Array.isArray(payload));

  const fromInstructions = parseMaybeJson(item?.instructions);
  if (isMicrolearningInstructionsObject(fromInstructions)) {
    candidates.push(fromInstructions);
  }

  const withToc = candidates.find(
    (payload) =>
      Array.isArray(payload?.table_of_contents?.chapters) &&
      payload.table_of_contents.chapters.length > 0
  );
  if (withToc) return withToc;

  const withVideoShape = candidates.find(isMicrolearningInstructionsObject);
  if (withVideoShape) return withVideoShape;

  return candidates[0] ?? {};
}

function resolveChapterThumbnailUrl(chapter = {}) {
  const candidate =
    chapter.thumbnail ??
    chapter.thumbnail_url ??
    chapter.thumbnailUrl ??
    chapter.chapter_thumbnail ??
    chapter.chapter_thumbnail_url ??
    "";
  return String(candidate ?? "").trim();
}

function mapVideoChapterStep(chapter) {
  return {
    chapter_title: chapter?.chapter_title ?? "",
    chapter_description: chapter?.chapter_description ?? "",
    chapter_summary: chapter?.chapter_summary ?? "",
    start_time: chapter?.start_time ?? "",
    end_time: chapter?.end_time ?? "",
    timestamp_ref:
      chapter?.timestamp_ref ??
      (chapter?.start_time && chapter?.end_time
        ? `${chapter.start_time} - ${chapter.end_time}`
        : ""),
    video_jump_url: chapter?.video_jump_url ?? "",
    thumbnail: resolveChapterThumbnailUrl(chapter),
    inspector_action: chapter?.inspector_action ?? chapter?.chapter_title ?? "",
    look_for: chapter?.look_for ?? chapter?.chapter_description ?? "",
    pass_criteria: chapter?.pass_criteria ?? "",
    fail_criteria: chapter?.fail_criteria ?? "",
  };
}

function resolveVideoDurationSeconds(microlearning) {
  const fromPayload = Number(
    microlearning?.video_duration ??
      microlearning?.duration_seconds ??
      microlearning?.table_of_contents?.duration_seconds ??
      microlearning?.table_of_contents?.video_duration
  );
  return Number.isFinite(fromPayload) && fromPayload > 0 ? fromPayload : null;
}

function resolveVideoSummaryBullets(microlearning) {
  const summaryField = microlearning?.microlearning_summary;
  if (summaryField && typeof summaryField === "object" && !Array.isArray(summaryField)) {
    return (summaryField.summary_bullets ?? [])
      .map((bullet) => String(bullet ?? "").trim())
      .filter(Boolean);
  }
  return [];
}

function resolveVideoSummaryLead(microlearning, item) {
  const videoSummary = String(
    microlearning?.videoSummary ?? microlearning?.table_of_contents?.videoSummary ?? ""
  ).trim();
  if (videoSummary) return videoSummary;

  const summaryField = microlearning?.microlearning_summary;
  if (summaryField && typeof summaryField === "object" && !Array.isArray(summaryField)) {
    const lead = String(summaryField.summary_lead ?? "").trim();
    if (lead) return lead;
  }

  const summary = resolveVideoSummary(microlearning, item);
  if (summary && typeof summary === "object" && !Array.isArray(summary)) {
    const overview = String(summary.overview ?? summary.text ?? "").trim();
    if (overview) return overview;
  }
  if (typeof summary === "string" && summary.trim()) {
    return summary.trim();
  }
  return "";
}

function resolveVideoChapterSteps(microlearning) {
  const tocChapters = microlearning?.table_of_contents?.chapters;
  if (Array.isArray(tocChapters) && tocChapters.length > 0) {
    return tocChapters.map(mapVideoChapterStep);
  }
  if (Array.isArray(microlearning?.chapters) && microlearning.chapters.length > 0) {
    return microlearning.chapters.map(mapVideoChapterStep);
  }
  return Array.isArray(microlearning?.steps) ? microlearning.steps : [];
}

function usesTocChapterCards(steps) {
  return steps.some((step) => String(step?.chapter_title ?? "").trim() !== "");
}

function parseVideoTimestampToSeconds(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.max(0, value);
  }
  const raw = String(value ?? "").trim();
  if (!raw) return 0;
  const timePart = raw.split("-")[0].trim();
  const segments = timePart.split(":").map((part) => parseInt(part, 10));
  if (segments.some((part) => Number.isNaN(part))) return 0;
  if (segments.length === 3) {
    return segments[0] * 3600 + segments[1] * 60 + segments[2];
  }
  if (segments.length === 2) {
    return segments[0] * 60 + segments[1];
  }
  return segments[0] ?? 0;
}

function formatVideoTimestampLabel(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

/** Maps TOC chapter steps to `VideoWithChaptersView` chapter row props. */
export function mapVideoChapterStepsForView(steps = []) {
  return steps.map((step, index) => {
    const startTimeSeconds = parseVideoTimestampToSeconds(step.start_time || step.timestamp_ref);
    const startTimeLabel =
      String(step.start_time ?? step.timestamp_ref ?? "")
        .trim()
        .split("-")[0]
        .trim() || formatVideoTimestampLabel(startTimeSeconds);
    const title =
      String(step.chapter_title ?? step.inspector_action ?? "").trim() ||
      `Chapter ${index + 1}`;
    const description =
      String(
        step.chapter_description ??
          step.chapter_summary ??
          step.look_for ??
          step.pass_criteria ??
          ""
      ).trim() || title;

    return {
      chapterNumber: index + 1,
      title,
      startTimeSeconds,
      startTimeLabel,
      description,
      thumbnailUrl: resolveChapterThumbnailUrl(step),
      videoJumpUrl: String(step.video_jump_url ?? "").trim(),
    };
  });
}

function resolveRawMicrolearningType(item = {}) {
  return norm(item?.active_microlearning_type) || norm(item?.proposed_microlearning_type);
}

export function isVideoWithTocCheckpoint(item = {}) {
  return resolveRawMicrolearningType(item) === "video_with_toc";
}

export function isVideoStepByStepCheckpoint(item = {}) {
  return resolveRawMicrolearningType(item) === "video_step_by_step_tutorial";
}

/** Video player + chapters + summary props for MDP-578 surfaces (employee, coach inspect, editor). */
export function resolveVideoWithChaptersViewProps(item = {}) {
  const { videoUrl, steps, videoDurationSeconds, summaryLead, summaryBullets } =
    resolveVideoMicrolearning(item);

  return {
    videoUrl: videoUrl || "",
    durationSeconds: Number(videoDurationSeconds) || 0,
    summaryLead: summaryLead || "",
    summaryBullets: summaryBullets || [],
    chapters: mapVideoChapterStepsForView(steps),
  };
}

function resolveVideoPlaybackUrl(item, microlearning) {
  const fromInstructions = extractPlaybackUrlFromMicrolearningInstructions(microlearning);

  const rawCandidates = [
    item?.active_microlearning,
    item?.microlearning,
    microlearning?.video_url,
    microlearning?.video,
    item?.video,
    item?.video_url,
  ];

  for (const candidate of rawCandidates) {
    if (candidate == null || candidate === "") continue;
    const asString = String(candidate).trim();
    if (!asString || isBareCloudflareStreamUid(asString)) continue;
    const fromIframe = extractVideoUrlFromIframeHtml(asString);
    if (fromIframe) return fromIframe;
    if (/^https?:\/\//i.test(asString)) return asString;
  }

  if (fromInstructions) return fromInstructions;

  const uidCandidates = [
    item?.active_video_source,
    item?.proposed_video_source,
    item?.active_microlearning_cloudflare_video_id,
    item?.microlearning_cloudflare_video_id,
    item?.activecloudflarevideo_id,
  ];

  for (const candidate of uidCandidates) {
    if (candidate == null || candidate === "") continue;
    const asString = String(candidate).trim();
    if (!asString) continue;
    if (!isBareCloudflareStreamUid(asString)) {
      const fromIframe = extractVideoUrlFromIframeHtml(asString);
      if (fromIframe) return fromIframe;
      if (/^https?:\/\//i.test(asString)) return asString;
    }
    const fromUid = resolveCloudflareIframeFromUid(asString, fromInstructions);
    if (fromUid) return fromUid;
  }

  return "";
}

function resolveVideoMicrolearning(item) {
  const microlearning = resolveVideoMicrolearningPayload(item);
  const videoUrl = resolveVideoPlaybackUrl(item, microlearning);
  const steps = resolveVideoChapterSteps(microlearning);
  const usesToc = usesTocChapterCards(steps);
  let summary = resolveVideoSummary(microlearning, item);

  // MicrolearningViewEdit only renders chapters in the summary layout branch.
  if (!summary && steps.length > 0) {
    const overview =
      overviewTextFromSummaryField(microlearning?.microlearning_summary) ||
      String(microlearning?.table_of_contents?.videoSummary ?? "").trim() ||
      String(microlearning?.videoSummary ?? "").trim();
    summary = { overview: overview || " " };
  }

  return {
    videoUrl,
    steps,
    summary,
    error: microlearning.error || null,
    showSteps: steps.length > 0 && !usesToc,
    videoDurationSeconds: resolveVideoDurationSeconds(microlearning),
    summaryLead: resolveVideoSummaryLead(microlearning, item),
    summaryBullets: resolveVideoSummaryBullets(microlearning),
  };
}

function resolveMicrolearningType(item) {
  const type = norm(item?.active_microlearning_type) || norm(item?.proposed_microlearning_type);
  return type === "video_source" ? "video" : type;
}

/** True when the checkpoint includes a video activity (even if playback URL resolves later). */
function resolveHasVideo(item, videoUrl) {
  const type = resolveMicrolearningType(item);
  if (type === "video") return true;
  if (type === "article" || type === "inspector_only") return false;

  if (
    Array.isArray(item?.activities) &&
    item.activities.some((activity) => norm(activity) === "video")
  ) {
    return true;
  }

  if (Boolean(videoUrl) || isMicrolearningInstructionsPayload(item?.microlearning_instructions)) {
    return true;
  }

  if (
    item?.set_to_microlearning_instructions &&
    isMicrolearningInstructionsPayload(item?.instructions)
  ) {
    return true;
  }

  if (item?.has_active_video != null) return Boolean(item.has_active_video);
  return false;
}

function resolveActiveQuizQuestions(item) {
  const quiz = parseMaybeJson(item?.active_quiz) ?? item?.active_quiz;
  if (Array.isArray(quiz)) return quiz;
  if (quiz && typeof quiz === "object" && Array.isArray(quiz.questions)) return quiz.questions;
  return null;
}

function activeQuizHasQuestions(item) {
  const questions = resolveActiveQuizQuestions(item);
  return Array.isArray(questions) && questions.length > 0;
}

function resolveActiveInspectionProtocol(item) {
  const candidates = [
    item?.active_inspection_protocol,
    item?.active_inspection_instructions,
    item?.proposed_inspection_protocol,
    item?.inspection_protocol,
    item?.inspection_instructions,
  ];

  for (const candidate of candidates) {
    if (candidate == null || candidate === "") continue;
    const parsed = parseMaybeJson(candidate) ?? candidate;
    if (parsed != null && parsed !== "") return parsed;
  }

  return null;
}

function hasQuiz(item) {
  const quizMode = norm(item?.active_quiz_mode) || norm(item?.quiz_mode);
  if (quizMode === "skip_quiz") return false;

  if (Array.isArray(item?.activities) && item.activities.length > 0) {
    return item.activities.some((activity) => norm(activity) === "quiz");
  }

  if (item?.has_active_quiz != null) return Boolean(item.has_active_quiz);
  if (activeQuizHasQuestions(item)) return true;
  const status = typeof item?.quiz === "string" ? item.quiz : item?.quiz_completion_status;
  return !["", "none", "null"].includes(norm(status));
}

function isQuizOnlyCheckpoint(item) {
  return (norm(item?.active_quiz_mode) || norm(item?.quiz_mode)) === "skip_inspection";
}

function hasInspection(item) {
  const quizMode = norm(item?.active_quiz_mode) || norm(item?.quiz_mode);
  if (quizMode === "skip_inspection") return false;

  if (Array.isArray(item?.activities) && item.activities.length > 0) {
    return item.activities.some((activity) => norm(activity) === "inspection");
  }

  if (item?.active_has_inspection_protocols != null) {
    return Boolean(item.active_has_inspection_protocols);
  }
  if (item?.has_active_inspection != null) return Boolean(item.has_active_inspection);
  return resolveInspectionSteps(item).length > 0;
}

/**
 * Checklist rows with no video/article/quiz and no protocol are Inspector-only.
 * Do not treat leftover `instructions` / `criteria` as an article, and do not
 * open an empty Inspection Protocol just because `has_active_inspection` is true.
 */
export function isInspectorOnlyCheckpoint(item = {}) {
  const microlearningType = resolveMicrolearningType(item);
  if (microlearningType === "inspector_only") return true;
  if (microlearningType === "video" || microlearningType === "article") return false;

  const flagsPresent =
    item?.has_active_article != null ||
    item?.has_active_video != null ||
    item?.has_active_quiz != null ||
    item?.has_active_inspection != null;

  if (!flagsPresent) return false;
  if (item.has_active_video || item.has_active_article || hasQuiz(item)) return false;
  if (resolveInspectionSteps(item).length > 0) return false;
  if (item.active_has_inspection_protocols === true) return false;
  return true;
}

/** Drawer sections for a checklist row that is Inspector-only on the list card. */
export function buildInspectorOnlyDrawerSections(item = {}) {
  return buildCheckpointDrawerSections({
    employee_prep_note: item?.employee_prep_note ?? null,
    active_microlearning_type: "inspector_only",
    has_active_inspection: true,
  });
}

function resolveArticleHeading(item) {
  const activeArticle = parseMaybeJson(item?.active_article) ?? item?.active_article;
  if (!activeArticle || typeof activeArticle !== "object") return "";
  return String(activeArticle.title || "").trim();
}

function resolveHasArticle(item) {
  const type = resolveMicrolearningType(item);
  if (type === "article") return true;
  if (type === "video" || type === "inspector_only") return false;

  if (item?.has_active_article != null) return Boolean(item.has_active_article);
  if (item?.active_article || item?.proposed_article) return true;
  if (isMicrolearningInstructionsPayload(item?.instructions)) return false;
  return Boolean(item?.instructions);
}

export function resolveQuizQuestions(item) {
  const activeQuizQuestions = resolveActiveQuizQuestions(item);
  const microlearning = item?.microlearning_instructions || {};
  const source =
    activeQuizQuestions ||
    item?.quiz_questions ||
    (isPlainObject(item?.quiz) ? item.quiz.questions : null) ||
    microlearning.quiz?.questions ||
    microlearning.questions ||
    item?.questions ||
    [];

  if (!Array.isArray(source)) return [];

  return source.map((question, questionIndex) => {
    const id = question?.id ?? question?.q_no ?? `question-${questionIndex + 1}`;
    const options = question?.q_answers || question?.options || question?.answers || [];
    return {
      id: String(id),
      text:
        question?.text || question?.q_text || question?.question || question?.question_text || "",
      options: (Array.isArray(options) ? options : []).map((option, optionIndex) => {
        const optionId = option?.id ?? option?.a_no ?? `${id}-option-${optionIndex + 1}`;
        const isCorrectRaw = option?.is_correct ?? option?.isCorrect;
        return {
          id: String(optionId),
          answerText:
            option?.answer_text || option?.answerText || option?.a_text || option?.text || "",
          isCorrect: isCorrectRaw === 1 || isCorrectRaw === true || isCorrectRaw === "1",
        };
      }),
    };
  });
}

function extractInspectionStepList(raw) {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== "object") return [];

  const candidates = [
    raw.steps_to_inspect,
    raw.instructions?.steps,
    raw.instructions?.instructions?.steps,
    raw.inspection_instructions?.instructions?.steps,
    raw.inspection_instructions?.steps,
    raw.steps,
  ];

  return candidates.find((list) => Array.isArray(list) && list.length > 0) || [];
}

/** The recorded per-step results of the checkpoint's inspection (Rule 14, MDP-648). */
function resolveStepResults(item, sequence, index) {
  const rows = Array.isArray(item?.inspection_steps?.steps) ? item.inspection_steps.steps : [];
  const row = rows.find((candidate) => candidate?.step_sequence === sequence);
  const states = Array.isArray(item?.inspection_step_states) ? item.inspection_step_states : [];
  const state = norm(row?.state ?? states[index]);
  const inspector = row?.inspected_by;
  const inspectorName =
    typeof inspector === "string"
      ? inspector
      : [inspector?.first_name, inspector?.last_name].filter(Boolean).join(" ");

  return {
    result: state === "passed" || state === "failed" ? state : "not_yet_inspected",
    comment: row?.comment || "",
    inspected_by_name: inspectorName.trim(),
    inspected_at: row?.inspected_at || "",
  };
}

export function resolveInspectionSteps(item) {
  const raw = resolveActiveInspectionProtocol(item);
  const steps = extractInspectionStepList(raw);

  return steps.map((step, index) => {
    const sequence = Number.isInteger(step?.step_sequence) ? step.step_sequence : index + 1;
    return {
      id: step?.id ?? `inspection-step-${index + 1}`,
      step_sequence: sequence,
      competence_category: step?.competence_category || step?.competenceCategory || "knowledge",
      inspector_action: step?.inspector_action || step?.inspectorAction || "",
      look_for: step?.look_for || step?.lookFor || "",
      pass_criteria: step?.pass_criteria || step?.passCriteria || "",
      fail_criteria: step?.fail_criteria || step?.failCriteria || "",
      timestamp_ref: step?.timestamp_ref || step?.timestamp || "",
      video_jump_url: step?.video_jump_url || step?.videoJumpUrl || "",
      ...resolveStepResults(item, sequence, index),
    };
  });
}

/**
 * @param {object} item checkpoint row
 * @param {{ includeResult?: boolean }} [options] `includeResult` appends the passed-confirmation
 *   step to quiz-only (SKIP_INSPECTION) checkpoints
 * @returns {Array<object>} drawer sections in walkthrough order
 *   (video/article → quiz → result | inspection, or a single inspector-only section)
 */
export function buildCheckpointDrawerSections(item = {}, { includeResult = false } = {}) {
  if (isInspectorOnlyCheckpoint(item)) {
    return [
      {
        id: "inspector-only",
        type: "inspector-only",
        employeePrepNote: item?.employee_prep_note ?? null,
      },
    ];
  }

  const microlearning = parseMaybeJson(item?.microlearning_instructions) || {};
  const sections = [];
  const {
    videoUrl,
    steps,
    summary,
    error,
    showSteps,
    videoDurationSeconds,
    summaryLead,
    summaryBullets,
  } = resolveVideoMicrolearning(item);
  const hasVideo = resolveHasVideo(item, videoUrl);
  const hasArticle = resolveHasArticle(item);
  const articleHeading = resolveArticleHeading(item);

  if (hasVideo) {
    sections.push({
      id: "video",
      type: "video",
      title: "Watch this Video",
      videoUrl: videoUrl || "",
      error,
      summary,
      steps,
      showSteps,
      videoDurationSeconds,
      summaryLead,
      summaryBullets,
      isStepByStepVideo: isVideoStepByStepCheckpoint(item),
    });
  } else if (hasArticle) {
    const articleSource = item?.active_article ?? item?.proposed_article;
    const articleContent = extractArticleContent(articleSource);
    const fallbackInstructions = isMicrolearningInstructionsPayload(item?.instructions)
      ? []
      : htmlToParagraphs(item?.instructions);
    const content = articleContent.length ? articleContent : fallbackInstructions;
    // List payloads often expose has_active_article without article body; keep the step.
    if (content.length || hasArticle) {
      sections.push(
        buildArticleSection({
          heading: articleHeading,
          content,
          articleHtml: resolveArticleHtml(item, articleSource, content),
          articleBody: resolveArticleBody(articleSource),
          languages: microlearning.languages || [],
          selectedLanguage:
            microlearning.selectedLanguage || microlearning.selected_language || null,
        })
      );
    }
  } else {
    const content = isMicrolearningInstructionsPayload(item?.instructions)
      ? []
      : htmlToParagraphs(item?.instructions);
    if (content.length) {
      sections.push(
        buildArticleSection({
          content,
          articleHtml: formatArticleHtml(item?.instructions),
          articleBody: null,
          languages: microlearning.languages || [],
          selectedLanguage:
            microlearning.selectedLanguage || microlearning.selected_language || null,
        })
      );
    }
  }

  if (hasQuiz(item)) {
    const questions = resolveQuizQuestions(item);
    sections.push({
      id: "quiz",
      type: "quiz",
      title: "Take the Quiz",
      questions,
    });

    // Quiz-only checkpoints (Shape 1) end on a passed confirmation.
    if (includeResult && isQuizOnlyCheckpoint(item)) {
      sections.push({
        id: "result",
        type: "result",
        title: "Your result",
        questionCount: questions.length,
      });
    }
  }

  const inspectionSteps = resolveInspectionSteps(item);
  if (hasInspection(item)) {
    sections.push({
      id: "inspection",
      type: "inspection",
      title: "Inspection Protocol",
      steps: inspectionSteps,
    });
  }

  if (!sections.length) {
    sections.push({
      id: "inspector-only",
      type: "inspector-only",
      employeePrepNote: item?.employee_prep_note ?? null,
    });
  }

  return sections;
}

/** 1-based step of the first section of `type`, or 1 when absent. */
export function findSectionStep(sections, type) {
  const index = (sections || []).findIndex((section) => section.type === type);
  return index >= 0 ? index + 1 : 1;
}

const MEDIA_DONE = new Set(["read", "watched", "passed", "complete", "completed"]);
const QUIZ_DONE = new Set(["passed"]);

function isStatusComplete(value, done) {
  return done.has(norm(value));
}

/**
 * Resume step from persisted `activity_statuses`. CTA ids still win when they
 * target a specific section (take/retake quiz, ask-inspection).
 *
 * @param {Array<object>} sections
 * @param {object} item checklist / detail row
 * @param {string} [actionId]
 * @returns {number} 1-based step
 */
export function findResumeStep(sections, item = {}, actionId = "") {
  if (actionId === "retake-quiz" || actionId === "take-quiz") {
    return findSectionStep(sections, "quiz");
  }
  if (actionId === "ask-inspection") {
    return findSectionStep(sections, "inspection");
  }

  const statuses = item?.activity_statuses || {};
  const list = sections || [];

  // A pass is final: a passed quiz-only checkpoint reopens on its result step.
  const resultIndex = list.findIndex((section) => section.type === "result");
  if (resultIndex >= 0 && isStatusComplete(statuses.quiz, QUIZ_DONE)) {
    return resultIndex + 1;
  }

  for (let index = 0; index < list.length; index += 1) {
    const type = list[index]?.type;
    if (type === "article" && !isStatusComplete(statuses.article, MEDIA_DONE)) {
      return index + 1;
    }
    if (type === "video" && !isStatusComplete(statuses.video, MEDIA_DONE)) {
      return index + 1;
    }
    if (type === "quiz" && !isStatusComplete(statuses.quiz, QUIZ_DONE)) {
      return index + 1;
    }
    if (type === "inspection" || type === "inspector-only") {
      return index + 1;
    }
  }

  return Math.max(list.length, 1);
}

/**
 * Maps checklist / detail inspection fields to the drawer alert status.
 * Checklist `activity_statuses.inspection` maps N/A to `passed` — chatter
 * overrides that when a "Not applicable because" note exists.
 */
export function resolveInspectionDrawerStatus(item = {}) {
  const raw = norm(
    item?.activity_statuses?.inspection ?? item?.inspection_status ?? item?.overall_status
  );
  if (["failed", "inspection_failed", "failed_inspection"].includes(raw)) return "failed";
  if (["not_applicable", "na", "n/a", "notapplicable"].includes(raw)) return "not_applicable";
  if (["passed", "complete", "completed"].includes(raw)) return "passed";
  return "pending";
}

function stripChatterNote(value) {
  return String(value ?? "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
}

export function formatInspectionDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Latest inspection verdict from checkpoint chatter (not a row field).
 * Notes are posted as "Passed.", "Failed because {reason}.", "Not applicable because {reason}.".
 *
 * @param {Array<object>} entries chatter `results`
 * @returns {{ status: string, reason: string, inspectorName: string, inspectionDate: string } | null}
 */
export function extractInspectionVerdictFromChatter(entries = []) {
  const list = Array.isArray(entries) ? entries : [];

  for (const entry of list) {
    const note = stripChatterNote(entry?.note || entry?.message || "");
    if (!note) continue;

    const inspectorName = String(entry?.recorded_by_name || entry?.recordedByName || "").trim();
    const inspectionDate = formatInspectionDate(entry?.recorded_on || entry?.recordedOn);

    const notApplicable = note.match(/^not applicable because\s+(.+?)\.?$/i);
    if (notApplicable) {
      return {
        status: "not_applicable",
        reason: notApplicable[1].trim(),
        inspectorName,
        inspectionDate,
      };
    }

    const failed = note.match(/^failed because\s+(.+?)\.?$/i);
    if (failed) {
      return {
        status: "failed",
        reason: failed[1].trim(),
        inspectorName,
        inspectionDate,
      };
    }

    if (/^passed\.?$/i.test(note)) {
      return { status: "passed", reason: "", inspectorName, inspectionDate };
    }
  }

  return null;
}

function resolveQuizOptionId(question, option, optionIndex, questionIndex) {
  const questionId = question?.id ?? `question-${questionIndex + 1}`;
  return String(option?.id ?? option?.a_no ?? `${questionId}-option-${optionIndex + 1}`);
}

/**
 * Maps drawer quiz-submit payload to server-graded `submitted_attempt` entries.
 *
 * @param {{ questions?: Array<object> }} quizSection
 * @param {Array<{ selectedOptionIds?: Array<string|number> }>} quizSubmitPayload
 * @returns {Array<{ selected_option_indexes: number[] }>}
 */
export function buildSubmittedQuizAttempt(quizSection, quizSubmitPayload) {
  const questions = quizSection?.questions || [];

  return (quizSubmitPayload || []).map((answer, questionIndex) => {
    const question = questions[questionIndex] || {};
    const options = question.options || [];
    const selectedIds = new Set((answer.selectedOptionIds || []).map(String));
    const selectedIndexes = options
      .map((option, optionIndex) =>
        selectedIds.has(resolveQuizOptionId(question, option, optionIndex, questionIndex))
          ? optionIndex
          : -1
      )
      .filter((index) => index >= 0);

    return { selected_option_indexes: selectedIndexes };
  });
}
